import { createDndDomain } from 'base/ui/dnd';
import { Rows } from 'base/ui/layout';
import { TextTiny } from 'base/ui/typography';
import { Comment, UserAvatar } from 'blocks/blocks_service';
import classNames from 'classnames';
import React from 'react';
import { Avatars } from './avatars';
import styles from './pin.css';
import { CommentContent } from './ui';

const PIN_MAX_AVATARS = 3;
const EXPANDED_MAX_AVATARS = 11;
const PIN_HEIGHT_PX = parseFloat(styles.pinHeight);

/**
 * DnD for the comment pins.
 * Source = threadId
 * Dest = coordinates
 **/
export const threadDndDomain = createDndDomain<
  number,
  { clientX: number; clientY: number }
>({
  isSelfDrop: () => false,
  cursorOffset: () => {
    return {
      cursorToHovering: { x: 0, y: 0 },
      // Use the constant height from css.  The measured height might be
      // the hover state which is taller than the default (dragging) state.
      cursorToBox: { x: 0, y: PIN_HEIGHT_PX },
    };
  },
});
export type ThreadDropTargetOpts = React.ComponentProps<
  typeof threadDndDomain.DropTarget
>['opts'];

type CommentPinProps = {
  avatars: UserAvatar[];
  comment?: Pick<Comment, 'author' | 'createdAt' | 'content'>;
  numReplies: number;
  isUnread?: boolean;
  isSelected: boolean;
  isEnlarged?: boolean;
  isFaded?: boolean;
  isInteractive?: boolean;
  onClick?: React.MouseEventHandler;
  onMouseEnter?: React.MouseEventHandler;
  onMouseLeave?: React.MouseEventHandler;
  draggableSource?: number | undefined;
  dropTargetOpts?: ThreadDropTargetOpts;
  x: number;
  y: number;
};

function _CommentPin(
  {
    avatars,
    comment,
    numReplies,
    isUnread,
    isSelected,
    isEnlarged,
    isFaded,
    x,
    y,
    draggableSource,
    dropTargetOpts,
    ...passthrough
  }: CommentPinProps,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const pinInner = (
    <div className={styles.pinInner}>
      <div className={classNames(styles.avatars, styles.avatarsCrossFade)}>
        <Avatars avatars={avatars} max={PIN_MAX_AVATARS} />
        <Avatars avatars={avatars} max={EXPANDED_MAX_AVATARS} />
      </div>
      {comment && (
        <Rows spacing={2}>
          <CommentContent comment={comment} contentLineClamp={1} />
          {numReplies > 0 && (
            <TextTiny>
              {numReplies} {numReplies === 1 ? 'reply' : 'replies'}
            </TextTiny>
          )}
        </Rows>
      )}
    </div>
  );
  return (
    <div
      ref={ref}
      className={classNames(styles.pinAnchor)}
      style={{
        ['--x' as string]: x + 'px',
        ['--y' as string]: y + 'px',
        ['--avatarCount' as string]: Math.min(avatars.length, PIN_MAX_AVATARS),
      }}
    >
      <threadDndDomain.Draggable
        source={draggableSource}
        dropTarget={dropTargetOpts}
      >
        <div
          className={classNames(
            styles.pin,
            isSelected && styles.isSelected,
            isEnlarged && styles.isEnlarged,
            isFaded && styles.isFaded,
            comment && !isSelected && !isEnlarged && styles.isInteractive,
            avatars.length > 1 && styles.useSeparateAvatarLine,
          )}
          {...passthrough}
        >
          <div className={styles.pinClip}>{pinInner}</div>
          {isUnread && (
            <>
              <div className={classNames(styles.unreadDot, styles.isBorder2)} />
              <div className={classNames(styles.unreadDot, styles.isBorder1)} />
              <div className={classNames(styles.unreadDot, styles.isCenter)} />
            </>
          )}
        </div>
      </threadDndDomain.Draggable>
    </div>
  );
}

export const CommentPin = React.forwardRef(_CommentPin);
